/**
 * OKS-401 — Premium badge tier derivation + visual treatments.
 *
 * Tier is NOT a persisted field. It is derived on the FE from data the
 * wire already carries (TRD KD-1):
 *   - milestone badges → a prestige ladder keyed by the milestone family
 *     identifier (PRD §3 milestone table),
 *   - custom badges    → the badge's `accent_color` token (PRD §3 accent
 *     table).
 *
 * No serializer `tier` field and no migration — keeping this a pure
 * visual layer. If product later wants authored tiers, that's a thin BE
 * follow-up; this module stays the single source of truth for the maps.
 *
 * `tierFor` is total: any unknown identifier / accent falls back to
 * `bronze` (mirrors `badgeIcon()`'s Award fallback — a stale FE bundle
 * never throws or renders blank).
 */

/** The four prestige tiers, lowest → highest. */
export type Tier = "bronze" | "silver" | "gold" | "special"

/**
 * Milestone family identifier → tier (PRD §3).
 *
 * OKS-976 — EXPORTED so coverage can enumerate the catalogue instead of
 * hand-listing families. That is not a convenience: this ticket fixed a
 * defect that survived for months precisely because a hand-written sample
 * of families omitted most of the affected ones. A test that iterates this
 * map cannot omit a family, and picks up family N+1 for free.
 *
 * Read expected tiers OUT of this map; do not recompute them by calling
 * `tierFor()`, which is the function under test on the surfaces that
 * consume it — that compares a thing to itself.
 */
export const MILESTONE_TIER_MAP: Record<string, Tier> = {
  first_mission: "bronze",
  missions_5: "bronze",
  missions_10: "silver",
  missions_25: "gold",
  top_performer: "special",
  perfect_streak_5: "special",
  // OKS-558 — net-new §14 achievement families. MUST mirror
  // `BadgeCatalog::MILESTONE_TIERS` (Ruby); `badge_tier_map_contract_spec`
  // guards the parity.
  views_10k: "silver",
  views_100k: "gold",
  streak_30: "gold",
  posts_100: "gold",
  // OKS-597 — prestige capstones. MUST mirror
  // `BadgeCatalog::MILESTONE_TIERS` (Ruby); `badge_tier_map_contract_spec`
  // guards the parity.
  brand_expert: "special",
  summit_club: "special",
  // OKS-966 — recognition family with no count ladder, so `special`. MUST
  // mirror `BadgeCatalog::MILESTONE_TIERS` (Ruby);
  // `badge_tier_map_contract_spec` guards the parity.
  first_publisher: "special",
  // OKS-967 — lifetime-cumulative like missions_5 (bronze) but gated on a
  // qualifier not every accept satisfies, so a rung above volume-only and below
  // the consecutive-run `special` families. MUST mirror
  // `BadgeCatalog::MILESTONE_TIERS` (Ruby); `badge_tier_map_contract_spec`
  // guards the parity.
  ultra_reactive: "silver",
  // OKS-964 — brand recognition with no ladder and no counter, so `special`
  // (mirrors first_publisher / top_performer). MUST mirror
  // `BadgeCatalog::MILESTONE_TIERS` (Ruby); `badge_tier_map_contract_spec`
  // guards the parity.
  coup_de_coeur: "special",
  // OKS-965 — the first-pass ladder sits one rung above the volume family at
  // the same number (missions_5 bronze / _10 silver / _25 gold), because it is
  // gated on a qualifier not every completion satisfies. MUST mirror
  // `BadgeCatalog::MILESTONE_TIERS` (Ruby); `badge_tier_map_contract_spec`
  // guards the transcription and `medallion_tier_source_spec` guards it by
  // PARSING this file.
  first_pass_5: "silver",
  first_pass_10: "gold",
  first_pass_25: "special",
}

/**
 * Custom-badge `accent_color` token → tier (PRD §3).
 *
 * OKS-976 — EXPORTED so coverage can build a deliberate FOIL accent per
 * milestone family: an accent whose derived tier disagrees with that
 * family's ladder tier. Without a foil, only the families whose shipped
 * accent happens to disagree can fail, and the handful whose accent
 * coincides with their ladder tier pass whether the surface is fixed or
 * broken — which is exactly how the OKS-976 defect stayed invisible.
 *
 * A foil exists for every tier: this map's value set spans all four, so
 * some accent always disagrees with any given expectation.
 */
export const ACCENT_TIER_MAP: Record<string, Tier> = {
  orange: "bronze",
  blue: "silver",
  purple: "silver",
  amber: "gold",
  yellow: "gold",
  green: "special",
}

/** Safe default for unknown identifiers / accents. */
const DEFAULT_TIER: Tier = "bronze"

/**
 * Runtime identifier → family key. TS twin of `BadgeCatalog.family_for`
 * (`app/services/badge_catalog.rb`) and its `EXPANDING_FAMILIES` table.
 *
 * ⚠️ EVERY PATTERN IS FULLY ANCHORED AND CARRIES ITS OWN FAMILY PREFIX.
 * A generic tail-strip (`/_\d+$/`) would rewrite `missions_5` → `missions`
 * and five other static family keys, silently dropping them out of the
 * tier map onto the `bronze` fallback. Keep this in lock-step with the
 * Ruby table.
 *
 * Exported because it is the ONLY copy of this rule on the FE — the brand
 * Awards tab used to hand-roll its own and rendered a raw i18n key for
 * suffixed identifiers (OKS-966).
 */
export function familyFor(identifier: string): string {
  const EXPANDING: [RegExp, string][] = [
    [/^top_performer_\d{4}_\d{2}$/, "top_performer"],
    [/^first_publisher_m\d+$/, "first_publisher"],
  ]
  for (const [pattern, family] of EXPANDING) {
    if (pattern.test(identifier)) return family
  }
  return identifier
}

/**
 * Derive a badge's tier. Milestones use the family ladder (authoritative
 * for milestones); customs use the accent map. Falls back to `bronze`.
 */
export function tierFor({
  kind,
  identifier,
  accentColor,
}: {
  kind: "milestone" | "custom"
  identifier?: string
  accentColor?: string
}): Tier {
  if (kind === "milestone" && identifier) {
    return MILESTONE_TIER_MAP[familyFor(identifier)] ?? DEFAULT_TIER
  }
  if (accentColor) {
    return ACCENT_TIER_MAP[accentColor] ?? DEFAULT_TIER
  }
  return DEFAULT_TIER
}

/** Recency window for the "NEW" chip. */
const NEW_WINDOW_DAYS = 7
const NEW_WINDOW_MS = NEW_WINDOW_DAYS * 24 * 60 * 60 * 1000

/**
 * True when a badge was earned within the last 7 days (PRD §3 — recency
 * only; no seen/unseen tracking exists). Defensive against null/invalid
 * dates and future timestamps.
 */
export function isNewlyEarned(earnedAt: string | null | undefined): boolean {
  if (!earnedAt) return false
  const earned = new Date(earnedAt).getTime()
  if (Number.isNaN(earned)) return false
  const age = Date.now() - earned
  if (age < 0) return false
  return age <= NEW_WINDOW_MS
}

/**
 * Per-tier Tailwind v4 utility strings (static CSS — no runtime image
 * generation, TRD §8 perf). `body` is the gradient; `glow` combines the
 * inner-shadow depth + highlight settle + outer glow into one `shadow-[]`
 * (only one shadow utility wins, so the layers are composed); `icon` is
 * the glyph contrast colour (≥3:1 on its tier body, UX Spec §6).
 *
 * NOTE: each value MUST appear as a complete literal here so Tailwind's
 * source scanner emits the class — never build these strings dynamically.
 */
export const TIER_CLASSES: Record<
  Tier,
  { body: string; glow: string; icon: string }
> = {
  bronze: {
    body: "bg-[linear-gradient(145deg,#e8a06a_0%,#b9743f_50%,#7c4a23_100%)]",
    glow: "shadow-[inset_0_2px_3px_rgba(255,255,255,0.5),inset_0_-3px_5px_rgba(0,0,0,0.35),0_0_16px_rgba(184,115,51,0.6)]",
    icon: "text-amber-50",
  },
  silver: {
    body: "bg-[linear-gradient(145deg,#f1f3f6_0%,#c2c7d0_50%,#878d99_100%)]",
    glow: "shadow-[inset_0_2px_3px_rgba(255,255,255,0.7),inset_0_-3px_5px_rgba(0,0,0,0.25),0_0_16px_rgba(148,163,184,0.6)]",
    icon: "text-slate-700",
  },
  gold: {
    body: "bg-[linear-gradient(145deg,#ffe680_0%,#f5c518_50%,#bf8f00_100%)]",
    glow: "shadow-[inset_0_2px_3px_rgba(255,255,255,0.7),inset_0_-3px_5px_rgba(0,0,0,0.3),0_0_22px_rgba(245,197,24,0.7)]",
    icon: "text-amber-950",
  },
  special: {
    body: "bg-[radial-gradient(circle_at_30%_25%,#f5d0fe_0%,#c084fc_40%,#7c3aed_100%)]",
    glow: "shadow-[inset_0_2px_3px_rgba(255,255,255,0.6),inset_0_-3px_6px_rgba(0,0,0,0.3),0_0_26px_rgba(168,85,247,0.75)]",
    icon: "text-white",
  },
}

/**
 * Locked treatment — inner-shadow depth only, no outer glow. Combined
 * with `grayscale` + reduced opacity on the medallion (UX Spec §3) so the
 * creator still reads the tier shape without the celebratory glow. No
 * separate grey assets.
 */
export const LOCKED_SHADOW =
  "shadow-[inset_0_2px_3px_rgba(255,255,255,0.25),inset_0_-3px_5px_rgba(0,0,0,0.3)]"
